/**
 * Brand theme generation (DESIGN.md §3.2 / §16): one primary colour → contrast-checked brand
 * tokens for light and dark, a selected-state tint and an 11-step scale.
 *
 * Guardrails (WCAG 2.2 AA, see docs/research/ui/branding.md):
 * - brand as text/icon/focus ring on every app surface: ≥ 4.5:1 (1.4.3; also covers 1.4.11's 3:1)
 * - text on the brand colour (buttons): ≥ 4.5:1, near-white or near-black is picked automatically
 * - tint (hover/selected rows) keeps ≥ 4.5:1 for the normal accent text
 * A colour that fails is adjusted in OKLCH lightness only (hue and chroma stay), the smallest
 * change that passes. Policy `strict` rejects instead of adjusting (light theme; the dark theme
 * always derives its own lightness).
 */
import { contrastRatio, floorRatio, oklchToRgb, parseHex, rgbToOklch, toHex, type Oklch, type Rgb } from './color.js';

export const MIN_TEXT_CONTRAST = 4.5;

/** Surfaces of packages/ui/src/styles/globals.css the brand colour sits on (keep in sync). */
const SURFACES = {
  light: [
    { l: 1, c: 0, h: 0 }, // --background / --card
    { l: 0.98, c: 0.003, h: 260 }, // --sidebar
    { l: 0.965, c: 0.004, h: 260 }, // --muted
  ],
  dark: [
    { l: 0.17, c: 0.008, h: 260 }, // --background
    { l: 0.2, c: 0.008, h: 260 }, // --card
    { l: 0.22, c: 0.008, h: 260 }, // --popover
    { l: 0.25, c: 0.008, h: 260 }, // --muted
  ],
} satisfies Record<Mode, Oklch[]>;

/** Text colours that may sit on the brand colour (--primary-foreground candidates). */
const ON_BRAND: Oklch[] = [
  { l: 0.985, c: 0, h: 0 },
  { l: 0.18, c: 0.02, h: 260 },
];

/** --accent-foreground per theme (fixed, not brandable). */
const ACCENT_TEXT = { light: { l: 0.25, c: 0.01, h: 260 }, dark: { l: 0.94, c: 0.005, h: 260 } } satisfies Record<Mode, Oklch>;

/** Built-in brand colour (globals.css --brand, light). */
const DEFAULT_BRAND: Oklch = { l: 0.52, c: 0.15, h: 255 };
export const DEFAULT_PRIMARY_COLOR = toHex(oklchToRgb(DEFAULT_BRAND));

export const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type ScaleStep = (typeof SCALE_STEPS)[number];
const SCALE_L = [0.97, 0.93, 0.87, 0.79, 0.7, 0.6, 0.51, 0.43, 0.36, 0.29, 0.22];
const SCALE_C = [0.15, 0.3, 0.5, 0.75, 0.9, 1, 1, 0.9, 0.75, 0.6, 0.45];

type Mode = 'light' | 'dark';

/** Colours of one theme, as `#rrggbb`. */
export interface BrandColors {
  /** --brand (→ --primary, --ring, --sidebar-primary). */
  brand: string;
  /** --brand-foreground (text on the brand). */
  brandForeground: string;
  /** --accent (hover / selected tint in the brand hue). */
  accent: string;
}

export interface ThemeCheck {
  /** Lowest brand-vs-surface contrast (brand used as text/icon). */
  brandOnSurface: number;
  /** Contrast of the brand foreground on the brand. */
  foregroundOnBrand: number;
  /** Contrast of the accent text on the tint. */
  textOnAccent: number;
  /** The brand colour had to be changed for this theme. */
  adjusted: boolean;
}

export interface BrandTheme {
  light: BrandColors;
  dark: BrandColors;
  /** 11-step scale in the brand hue (--brand-50 … --brand-950). */
  scale: Record<`${ScaleStep}`, string>;
}

export interface BrandThemeReport {
  /** The colour as entered (normalized `#rrggbb`). */
  input: string;
  light: ThemeCheck;
  dark: ThemeCheck;
  minContrast: number;
}

export interface GeneratedTheme {
  theme: BrandTheme;
  report: BrandThemeReport;
}

export type ContrastPolicy = 'adjust' | 'strict';

export class ContrastError extends Error {
  constructor(readonly report: BrandThemeReport) {
    super(`The colour ${report.input} has too little contrast in the light theme (needs ${MIN_TEXT_CONTRAST}:1)`);
    this.name = 'ContrastError';
  }
}

const rgb = (color: Oklch): Rgb => oklchToRgb(color);
const minContrast = (color: Rgb, against: readonly Oklch[]) => Math.min(...against.map((other) => contrastRatio(color, rgb(other))));

function bestForeground(brand: Rgb): { color: Rgb; ratio: number } {
  return ON_BRAND.map((candidate) => ({ color: rgb(candidate), ratio: contrastRatio(brand, rgb(candidate)) })).reduce((a, b) =>
    b.ratio > a.ratio ? b : a,
  );
}

function tint(base: Oklch, mode: Mode): Oklch {
  return mode === 'light' ? { l: 0.95, c: Math.min(0.04, base.c * 0.1), h: base.h } : { l: 0.28, c: Math.min(0.05, base.c * 0.13), h: base.h };
}

function passes(color: Rgb, mode: Mode): boolean {
  return minContrast(color, SURFACES[mode]) >= MIN_TEXT_CONTRAST && bestForeground(color).ratio >= MIN_TEXT_CONTRAST;
}

/** The smallest lightness change (darker in light, lighter in dark) that passes. */
function fit(input: Rgb, mode: Mode): { color: Rgb; adjusted: boolean } {
  if (passes(input, mode)) return { color: input, adjusted: false };
  const base = rgbToOklch(input);
  const step = mode === 'light' ? -0.005 : 0.005;
  for (let l = base.l + step; l >= 0 && l <= 1; l += step) {
    const candidate = rgb({ ...base, l });
    if (passes(candidate, mode)) return { color: candidate, adjusted: true };
  }
  // Unreachable for valid input (black/white always pass); fall back to the extreme.
  return { color: rgb({ ...base, l: mode === 'light' ? 0 : 1 }), adjusted: true };
}

function themeFor(input: Rgb, mode: Mode): { colors: BrandColors; check: ThemeCheck } {
  const { color, adjusted } = fit(input, mode);
  const foreground = bestForeground(color);
  const accent = rgb(tint(rgbToOklch(color), mode));
  return {
    colors: { brand: toHex(color), brandForeground: toHex(foreground.color), accent: toHex(accent) },
    check: {
      brandOnSurface: floorRatio(minContrast(color, SURFACES[mode])),
      foregroundOnBrand: floorRatio(foreground.ratio),
      textOnAccent: floorRatio(contrastRatio(accent, rgb(ACCENT_TEXT[mode]))),
      adjusted,
    },
  };
}

function scaleFor(input: Rgb): BrandTheme['scale'] {
  const base = rgbToOklch(input);
  return Object.fromEntries(
    SCALE_STEPS.map((step, i) => [String(step), toHex(rgb({ l: SCALE_L[i]!, c: base.c * SCALE_C[i]!, h: base.h }))]),
  ) as BrandTheme['scale'];
}

/** Normalizes `#RRGGBB` → `#rrggbb`; undefined when it is not a 6-digit hex colour. */
export function normalizeHex(value: string): string | undefined {
  const parsed = parseHex(value);
  return parsed ? toHex(parsed) : undefined;
}

/**
 * Generates the brand theme for a `#rrggbb` colour. Throws `TypeError` for an invalid colour and
 * `ContrastError` (policy `strict`) when the light theme would need an adjustment.
 */
export function generateBrandTheme(hex: string, policy: ContrastPolicy = 'adjust'): GeneratedTheme {
  const input = parseHex(hex);
  if (!input) throw new TypeError(`Not a #rrggbb colour: ${hex}`);
  const light = themeFor(input, 'light');
  const dark = themeFor(input, 'dark');
  const report: BrandThemeReport = {
    input: toHex(input),
    light: light.check,
    dark: dark.check,
    minContrast: Math.min(light.check.brandOnSurface, light.check.foregroundOnBrand, dark.check.brandOnSurface, dark.check.foregroundOnBrand),
  };
  if (policy === 'strict' && light.check.adjusted) throw new ContrastError(report);
  return { theme: { light: light.colors, dark: dark.colors, scale: scaleFor(input) }, report };
}
